/**
 * The five arenas.
 *
 * ── Why they live here ───────────────────────────────────────────────────
 *
 * These were authored in the client's arena mockup, which was the right place
 * to DESIGN them: it renders MapDefs through the real renderer at the real
 * camera, so what you approve is what you get. It is the wrong place to KEEP
 * them. A map the client owns is a map the server does not have, and the server
 * is the one resolving hits. `createArena` is the single point where a map id
 * becomes geometry, both runtimes call it, and now these are behind it.
 *
 * The mockup still renders them — by calling this, rather than by keeping a
 * second copy that drifts.
 *
 * ── Determinism is not optional here ─────────────────────────────────────
 *
 * Every value below is a pure function of a grid index. No `Math.random`, no
 * clock, and no `Math.atan2` — trig that is not `dsin`/`dcos` is not guaranteed
 * bit-identical between two engines, and this geometry decides where the walls
 * are. A map that differs by one platform between client and server is a client
 * predicting against an arena the server is not resolving in.
 *
 * ── The shared design language ───────────────────────────────────────────
 *
 * STEP / COVER / WALL are the three heights that mean something: a thing you
 * run over, a thing you hide behind, a thing that stops you.
 *
 * Pools are thin slabs lying on the deck rather than a floor kind, because a
 * hazard is a PLACE — walk around it, fight over it, finish someone in it —
 * where v1's lava floor was a state you were simply in. They sit flush at 0.12:
 * at 0.4 a mend pad was a plinth you climbed onto, and a pad two players can
 * contest from the same height is a fight rather than a perch.
 */
import { dcos, dsin, TWO_PI } from "./math.js";
import { DEFAULT_ARENA_RADIUS, FLOOR_RADIUS_FACTOR, createFlatMap, makePlatform, } from "./map.js";
import { PROP_SIZES, facingCenter } from "./props.js";
/** Heights that mean something: run over / hide behind / stopped by. */
const STEP = 1.6;
const COVER = 3.6;
const WALL = 5.4;
/** Pools lie flush in the deck. See the header. */
const POOL_H = 0.12;
function hazard(id, x, z, w, d, dps = 18) {
    return makePlatform(id, x, POOL_H / 2, z, w, POOL_H, d, { kind: "hazard", dps });
}
function mend(id, x, z, r = 5, hps = 14) {
    return makePlatform(id, x, POOL_H / 2, z, r, POOL_H, r, { kind: "heal", hps });
}
/**
 * A prop, sized from the art rather than from the caller.
 *
 * `PROP_SIZES` is the contract so a map cannot author a desk with a rack's
 * footprint. A quarter turn swaps the footprint; anything else cannot be
 * represented by an axis-aligned box at all, which is why `facingCenter`
 * returns only quarter turns.
 */
function prop(id, kind, x, z, rotY = 0, opts = {}) {
    const size = PROP_SIZES[kind];
    if (!size)
        throw new Error(`unknown prop kind: ${kind}`);
    const [w, h, d] = size;
    const turned = Math.abs(Math.round(rotY / (Math.PI / 2))) % 2 === 1;
    const platform = makePlatform(id, x, (opts.y ?? 0) + h / 2, z, turned ? d : w, h, turned ? w : d);
    platform.model = kind;
    platform.rotY = rotY;
    platform.solid = opts.solid ?? true;
    return platform;
}
/** Scenery: drawn, never collided with. */
function decor(id, kind, x, z, rotY = 0, opts = {}) {
    return prop(id, kind, x, z, rotY, { ...opts, solid: false });
}
function ringSpawns(radius, count, factor = 0.8) {
    const out = [];
    for (let i = 0; i < count; i++) {
        const a = (i / count) * TWO_PI;
        out.push({ x: radius * factor * dsin(a), y: 6, z: radius * factor * dcos(a) });
    }
    return out;
}
function shell(id, name, platforms, radius, playerCount) {
    return {
        id,
        name,
        platforms,
        windZones: [],
        spawns: ringSpawns(radius, playerCount),
        floor: { kind: "solid", y: 0 },
        killY: -50,
        centerX: 0,
        centerZ: 0,
        startRadius: radius,
        floorRadius: radius * FLOOR_RADIUS_FACTOR,
        zoneCenterJitter: radius * 0.3,
    };
}
/**
 * The edge of the room.
 *
 * Two rings of desks pressed up against the play boundary, where they frame the
 * fight, and an unbroken run of machines at the rim, where it closes the room.
 * Open deck between them on purpose: `floorRadius` is 1.5x `startRadius`, so a
 * third of the deck is outside the zone before the match begins, and ground
 * nobody fights on should read as ground nobody fights on. An earlier version
 * carpeted that ring with 130 desks and made dead space look like somewhere
 * to go.
 *
 * All non-solid and all outside `startRadius`. An arena that plays differently
 * because somebody added a pot plant is an arena nobody can balance.
 */
function dressPerimeter(out, radius, seed = 0) {
    let n = 0;
    for (const [ringR, count] of [
        [radius * 1.04, 20],
        [radius * 1.15, 22],
    ]) {
        for (let i = 0; i < count; i++) {
            const a = (i / count) * TWO_PI + seed * 0.31 + ringR * 0.017;
            const x = ringR * dsin(a);
            const z = ringR * dcos(a);
            const face = facingCenter(x, z);
            out.push(decor(`row_desk_${n}`, "desk", x, z, face));
            if (i % 3 === 0)
                out.push(decor(`row_chair_${n}`, "chair", x * 1.05, z * 1.05, face));
            n++;
        }
    }
    // Inset by the WIDEST prop on the wall, not by a guessed margin. At 0.9 the
    // monitor banks — 4.2 wide, so 2.1 of half-extent — reached 38.7 on a deck
    // of 37.5 and hung over the drop. Furniture floating above the void is the
    // kind of thing that reads as a bug even though nothing collides with it.
    const wallHalf = Math.max(PROP_SIZES['screens'][0], PROP_SIZES['rack'][0]) / 2;
    const wallR = radius * FLOOR_RADIUS_FACTOR - wallHalf - 0.4;
    const wallCount = Math.max(12, Math.round((TWO_PI * wallR) / 2.6));
    for (let i = 0; i < wallCount; i++) {
        const a = (i / wallCount) * TWO_PI;
        const x = wallR * dsin(a);
        const z = wallR * dcos(a);
        out.push(decor(`wall_${i}`, i % 6 === 0 ? "screens" : "rack", x, z, facingCenter(x, z)));
    }
    for (let i = 0; i < 5; i++) {
        const a = (i / 5) * TWO_PI + 0.7 + seed;
        const rr = radius * 1.16;
        out.push(decor(`row_plant_${i}`, "plant", rr * dsin(a), rr * dcos(a)));
    }
}
/** A line of props laid end to end — a wall built of repeated office parts. */
function propRow(out, id, kind, x0, z0, x1, z1, rotY, gap = 0.35) {
    const w = PROP_SIZES[kind][0];
    const dx = x1 - x0;
    const dz = z1 - z0;
    const len = Math.sqrt(dx * dx + dz * dz);
    const n = Math.max(1, Math.floor(len / (w + gap)));
    for (let i = 0; i < n; i++) {
        const t = n === 1 ? 0.5 : i / (n - 1);
        out.push(prop(`${id}_${i}`, kind, x0 + dx * t, z0 + dz * t, rotY));
    }
}
/**
 * THE CHART — candlestick pillars in thickets, with lanes between.
 *
 * Hosts SELLOFF and RALLY: candles drop to the deck or shoot up, so the whole
 * arena is one primitive repeated and a mutation that changes heights reads
 * across the entire map at once.
 *
 * Not a uniform grid. A full grid at this pitch is a maze — 104 candles, a wall
 * of blocks with no floor visible and nowhere to rotate. The field is cut into
 * 3x3 thickets separated by one empty cell: inside a thicket candles sit 1.6
 * apart, between thickets the gap opens to 5.2 and becomes an avenue, and a
 * quarter of the blocks are dropped entirely and become plazas.
 */
export function createChartMap(opts) {
    const radius = opts?.radius ?? DEFAULT_ARENA_RADIUS;
    const playerCount = opts?.playerCount ?? 8;
    // Five bands rather than three. In the reference no two neighbouring columns
    // are the same height and the range runs from knee to well overhead — that
    // silhouette variety is most of what makes it read as a CHART rather than as
    // a field of identical blocks. STEP and WALL still mean what they mean; the
    // two in between are chart values, not gameplay heights.
    const BANDS = [STEP, 2.6, COVER, 4.4, WALL];
    // 2.0 at 3.6 leaves a 1.6 lane against the player's 1.0 diameter. The match
    // camera only sees about 17 units across, so a wider pitch shows three
    // candles and an expanse of deck.
    const W = 1.5;
    const PITCH = 3.6;
    // The hazard fills the lane between two rows, but a lane is now 2.1 wide and
    // the reference strip is a thin neon line, not a band you could stand beside.
    // Narrow it deliberately: a hazard you must commit to crossing reads better
    // than one you can shuffle around inside.
    const LANE = 1.1;
    const platforms = [];
    const half = Math.floor(radius / PITCH);
    for (let gx = -half; gx <= half; gx++) {
        for (let gz = -half; gz <= half; gz++) {
            const x = gx * PITCH;
            const z = gz * PITCH;
            if (x * x + z * z > (radius - W) * (radius - W))
                continue;
            if (gx === 0 && gz === 0)
                continue; // the mend pad's cell
            const bx = Math.floor((gx + 1) / 4);
            const bz = Math.floor((gz + 1) / 4);
            const ix = (((gx + 1) % 4) + 4) % 4;
            const iz = (((gz + 1) % 4) + 4) % 4;
            if (ix === 3 || iz === 3)
                continue; // the avenue
            if (Math.abs(bx * 7 + bz * 13 + 5) % 4 === 0)
                continue; // the plaza
            const band = BANDS[Math.abs((gx * 3 + gz * 5 + 5) % BANDS.length)] ?? BANDS[0];
            const distFrac = Math.sqrt(x * x + z * z) / radius;
            if (distFrac > 0.8 && (gx + gz) % 2 === 0)
                continue;
            platforms.push(makePlatform(`candle_${gx}_${gz}`, x, band / 2, z, W, band, W));
        }
    }
    // The hazard is a LANE, not a slab: a lit strip exactly filling the gap
    // between two rows, so it reads as one of the ROUTES being switched on.
    // Mirrored, though the reference shows one — a map where the punished route
    // exists on one side only is not fair, and a reference is not a balance doc.
    const laneZ = PITCH * 1.5;
    platforms.push(hazard("pool_lane_a", 0, -laneZ, radius * 1.7, LANE));
    platforms.push(hazard("pool_lane_b", 0, laneZ, radius * 1.7, LANE));
    platforms.push(mend("mend_core", 0, 0, PITCH - W + 1.4));
    for (let i = 0; i < 4; i++) {
        const a = Math.PI / 4 + (i / 4) * TWO_PI;
        const rr = radius * 0.52;
        const x = rr * dsin(a);
        const z = rr * dcos(a);
        platforms.push(prop(`rack_${i}`, "rack", x, z, facingCenter(x, z)));
    }
    dressPerimeter(platforms, radius, 0);
    return shell("spire", "The Chart", platforms, radius, playerCount);
}
/**
 * THE PIT — the open-outcry floor as concentric steps down to a centre.
 *
 * Hosts HOT TAPE. The middle is LOW rather than high, because a trading pit is
 * a bowl and a bowl means the safest ground is the rim — which fights the
 * closing zone. The zone pushes you in; the floor punishes the middle.
 */
export function createPitMap(opts) {
    const radius = opts?.radius ?? DEFAULT_ARENA_RADIUS;
    const playerCount = opts?.playerCount ?? 8;
    const platforms = [];
    const RINGS = 3;
    for (let r = 0; r < RINGS; r++) {
        const ringRadius = radius * (0.36 + r * 0.2);
        const height = STEP + r * 0.9;
        const seats = 10 + r * 4;
        for (let i = 0; i < seats; i++) {
            if (i % 4 === 3)
                continue; // a ring you can run through beats a wall
            const a = (i / seats) * TWO_PI + r * 0.21;
            platforms.push(makePlatform(`tier_${r}_${i}`, ringRadius * dsin(a), height / 2, ringRadius * dcos(a), 5.2, height, 3.2));
        }
    }
    platforms.push(makePlatform("desk", 0, STEP / 2, 0, 8, STEP, 8));
    // Molten tape follows the rings, set in the gaps between tiers, sized to the
    // ~1.8 units of deck actually exposed there — a deeper pool is two thirds
    // buried under a terrace and reads as red slivers. Every third segment is
    // missing: an unbroken ring of damage severs the bowl into rings nobody can
    // cross, and you have to be able to get through, at a price.
    for (let r = 0; r < RINGS - 1; r++) {
        const gapRadius = radius * (0.36 + r * 0.2 + 0.1);
        const segs = 8 + r * 2;
        for (let i = 0; i < segs; i++) {
            if (i % 3 === 2)
                continue;
            const a = (i / segs) * TWO_PI + r * 0.4;
            platforms.push(hazard(`molten_${r}_${i}`, gapRadius * dsin(a), gapRadius * dcos(a), 7.0, 1.8));
        }
    }
    platforms.push(mend("mend_desk", 0, 0, 4.5));
    platforms.push(prop("desk_w", "desk", -2.2, 0.6, Math.PI / 2, { y: STEP }));
    platforms.push(prop("desk_e", "desk", 2.2, 0.6, -Math.PI / 2, { y: STEP }));
    platforms.push(decor("desk_chair_w", "chair", -2.2, 2.4, 0, { y: STEP }));
    platforms.push(decor("desk_chair_e", "chair", 2.2, 2.4, 0, { y: STEP }));
    platforms.push(prop("desk_screens", "screens", 0, -2.6, 0, { y: STEP }));
    for (let i = 0; i < 10; i++) {
        const a = (i / 10) * TWO_PI + 0.3;
        const rr = radius * 0.78;
        const x = rr * dsin(a);
        const z = rr * dcos(a);
        platforms.push(prop(`tier_desk_${i}`, "desk", x, z, facingCenter(x, z), { y: STEP + 1.8 }));
    }
    dressPerimeter(platforms, radius, 1);
    return shell("crucible", "The Pit", platforms, radius, playerCount);
}
/**
 * THE EXCHANGE — corridors and doors.
 *
 * Hosts CIRCUIT BREAKER, the one mutation that changes TOPOLOGY rather than
 * height. The spines are rows of cubicle partitions rather than four long
 * slabs: a corridor walled with repeated office parts reads as a floor plan,
 * and a repeated unit gives the eye a scale to measure everything else by.
 */
export function createExchangeMap(opts) {
    const radius = opts?.radius ?? DEFAULT_ARENA_RADIUS;
    const playerCount = opts?.playerCount ?? 8;
    const platforms = [];
    const r = radius;
    const half = r * 0.525;
    propRow(platforms, "spine_n", "cubicle", -half, -r * 0.34, half, -r * 0.34, 0);
    propRow(platforms, "spine_s", "cubicle", -half, r * 0.34, half, r * 0.34, 0);
    propRow(platforms, "spine_e", "cubicle", r * 0.34, -half, r * 0.34, half, Math.PI / 2);
    propRow(platforms, "spine_w", "cubicle", -r * 0.34, -half, -r * 0.34, half, Math.PI / 2);
    for (const [sx, sz] of [
        [1, 1],
        [1, -1],
        [-1, 1],
        [-1, -1],
    ]) {
        platforms.push(prop(`hub_${sx}_${sz}`, "rack", sx * r * 0.34, sz * r * 0.34, 0));
    }
    // The gaps a CIRCUIT BREAKER would close, marked on the FLOOR. As blocks they
    // were the last graybox in this arena; a threshold you can see and walk over
    // says "door" where an obstacle says "wall, but small".
    const threshold = (id, x, z, sx, sz) => {
        const plate = makePlatform(id, x, 0.05, z, sx, 0.1, sz);
        plate.solid = false;
        return plate;
    };
    platforms.push(threshold("door_n", 0, -r * 0.34, 5, 1.6));
    platforms.push(threshold("door_s", 0, r * 0.34, 5, 1.6));
    platforms.push(threshold("door_e", r * 0.34, 0, 1.6, 5));
    platforms.push(threshold("door_w", -r * 0.34, 0, 1.6, 5));
    dressPerimeter(platforms, radius, 2);
    return shell("grid", "The Exchange", platforms, radius, playerCount);
}
/**
 * BUBBLE — islands over nothing.
 *
 * Hosts POP: islands leave, one at a time, and the floor beneath is a kill
 * plane. The only arena where a mutation can END you rather than inconvenience
 * you, which is why the islands are large and the gaps jumpable — the drama has
 * to come from an island vanishing, not from missing a jump nobody could make.
 *
 * No perimeter ring: the islands float over a void, so a wall of server racks
 * around the rim would be standing on the nothing it is meant to surround.
 */
export function createBubbleMap(opts) {
    const radius = opts?.radius ?? DEFAULT_ARENA_RADIUS;
    const playerCount = opts?.playerCount ?? 8;
    const platforms = [];
    const COUNT = 7;
    for (let i = 0; i < COUNT; i++) {
        const a = (i / COUNT) * TWO_PI;
        const rr = radius * 0.58;
        const h = STEP + (i % 3) * 0.7;
        const x = rr * dsin(a);
        const z = rr * dcos(a);
        platforms.push(makePlatform(`isle_${i}`, x, h / 2, z, 9, h, 9));
        platforms.push(prop(`isle_desk_${i}`, "desk", x, z, facingCenter(x, z), { y: h }));
        platforms.push(decor(`isle_plant_${i}`, "plant", x + 3.0, z + 3.0, 0, { y: h }));
    }
    platforms.push(makePlatform("isle_core", 0, STEP / 2, 0, 11, STEP, 11));
    platforms.push(mend("mend_core", 0, 0, 4));
    platforms.push(prop("core_screens", "screens", 0, -3.4, 0, { y: STEP }));
    platforms.push(decor("core_coins", "coins", 3.2, 3.2, 0, { y: STEP }));
    const map = shell("drift", "Bubble", platforms, radius, playerCount);
    // The floor is the void, and it reads as pale sky rather than a dark hole:
    // an island leaving should feel like it drifted off, not like the lights
    // went out underneath it.
    map.floor = { kind: "none" };
    return map;
}
/**
 * HFT FLOOR — long parallel belts.
 *
 * Hosts ORDER FLOW, and is the only arena whose signature intervention already
 * exists in the sim. The belts are real `conveyor` surfaces rather than plain
 * slabs, which states the arena's whole idea before the mutation ever fires:
 * the ground tells you which way it will carry you. Directions alternate by
 * lane, because a floor that all runs one way is a travelator, while lanes that
 * disagree make crossing them a decision.
 */
export function createHftMap(opts) {
    const radius = opts?.radius ?? DEFAULT_ARENA_RADIUS;
    const playerCount = opts?.playerCount ?? 8;
    const platforms = [];
    const LANES = 5;
    for (let i = 0; i < LANES; i++) {
        const z = (i - (LANES - 1) / 2) * (radius * 0.34);
        const len = Math.sqrt(Math.max(0, radius * radius - z * z)) * 1.7;
        if (len < 6)
            continue;
        // The middle belt runs hot: on a map where the ground already moves you,
        // one lane you must not be carried into is the whole tension.
        const isHot = i === Math.floor(LANES / 2);
        if (isHot) {
            platforms.push(hazard(`belt_${i}`, 0, z, len, 5.5, 14));
        }
        else {
            const surface = { kind: "conveyor", dirX: i % 2 === 0 ? 1 : -1, dirZ: 0, speed: 5 };
            platforms.push(makePlatform(`belt_${i}`, 0, STEP / 2, z, len, STEP, 5.5, surface));
        }
        if (i < LANES - 1) {
            const dz = z + radius * 0.17;
            const halfLen = len * 0.31;
            propRow(platforms, `aisle_${i}`, "rack", -halfLen, dz, halfLen, dz, 0, 1.6);
        }
    }
    dressPerimeter(platforms, radius, 4);
    return shell("foundry", "HFT Floor", platforms, radius, playerCount);
}
/** Every arena this build can serve, in review order. */
export const ARENA_IDS = ["spire", "crucible", "grid", "drift", "foundry"];
/**
 * The arena a match plays on when nothing has chosen one.
 *
 * It used to be the graybox, `"flat"`, hardcoded in five separate places — the
 * gym, the local connection, the lobby's attract backdrop and twice in
 * ScrimRoom. Which meant the five real arenas were built, tested, themed and
 * shipped, and then nothing ever asked for one: every match, every playtest and
 * the lobby's own backdrop ran on the bowl.
 *
 * The graybox still exists and is still the right answer for tests that build
 * their own geometry (`createFlatMap({ bare: true })`). It is the wrong answer
 * for a player, and it should never have been the fallback that five call sites
 * each decided on independently.
 *
 * The Chart because it is the arena built most directly against the reference,
 * and because its candles are the clearest read on SELLOFF and RALLY — the
 * mutation pair the game master leans on hardest.
 */
export const DEFAULT_ARENA_ID = "spire";
/**
 * The one place a map id becomes geometry.
 *
 * BOTH runtimes call this. The client used to build its own map and the server
 * its own fallback, which happened to agree only because there was a single
 * map — the moment a second one existed, that arrangement would have produced a
 * client predicting against a different arena from the one the server resolves
 * hits in. This makes that structurally impossible rather than a convention.
 *
 * It lives here rather than in map.ts so the dependency runs one way: arenas
 * know about map primitives, primitives know nothing about arenas.
 *
 * An unknown id falls back to the graybox rather than throwing. A map that has
 * not been authored yet should be playable, not a failed join.
 */
export function createArena(mapId, opts) {
    switch (mapId) {
        case "spire":
            return createChartMap(opts);
        case "crucible":
            return createPitMap(opts);
        case "grid":
            return createExchangeMap(opts);
        case "drift":
            return createBubbleMap(opts);
        case "foundry":
            return createHftMap(opts);
        default: {
            const map = createFlatMap(opts);
            map.id = mapId;
            return map;
        }
    }
}
//# sourceMappingURL=arenas.js.map